import React from 'react';
import { Link } from 'react-router-dom';
import { Project } from '../../data/projects';
import { LandingContent, hasProjectPage } from '../../data/projectContent';
import './ProjectTemplates.css';

interface Props {
  project: Project;
  content?: LandingContent;
}

// Helper to render text with markdown-style links [text](url)
const renderTextWithLinks = (text: string): React.ReactNode => {
  const linkPattern = /\[([^\]]+)\]\(([^)]+)\)/g;
  const parts: React.ReactNode[] = [];
  let lastIndex = 0;
  let match;

  while ((match = linkPattern.exec(text)) !== null) {
    // Add text before the link
    if (match.index > lastIndex) {
      parts.push(text.slice(lastIndex, match.index));
    }
    // Add the link
    parts.push(
      <a key={match.index} href={match[2]} target="_blank" rel="noopener noreferrer">
        {match[1]}
      </a>
    );
    lastIndex = match.index + match[0].length;
  }

  // Add remaining text
  if (lastIndex < text.length) {
    parts.push(text.slice(lastIndex));
  }

  return parts.length > 0 ? parts : text;
};

const ProjectLandingTemplate: React.FC<Props> = ({ project, content }) => {
  const hasPrivacy = hasProjectPage(project.slug, 'privacy');
  const hasSupport = hasProjectPage(project.slug, 'support');

  return (
    <div className="project-page project-landing-page">
      <div className="project-page-container">
        {/* Hero Section */}
        <div className="project-hero">
          <Link to="/projects" className="project-back-link">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="m15 18-6-6 6-6" />
            </svg>
            All Projects
          </Link>
          <h1>{content?.heroTitle || project.name}</h1>
          <p className="project-tagline">{content?.heroSubtitle || project.tagline}</p>
          <p className="project-description">{project.fullDescription}</p>

          {/* CTA Buttons */}
          <div className="project-cta-buttons">
            {content?.ctaUrl ? (
              <a href={content.ctaUrl} className="project-cta-primary" target="_blank" rel="noopener noreferrer">
                {content.ctaText || 'Learn More'}
              </a>
            ) : project.links[0] && (
              <a href={project.links[0].url} className="project-cta-primary" target="_blank" rel="noopener noreferrer">
                {project.links[0].label}
              </a>
            )}
            {hasPrivacy && (
              <Link to={`/projects/${project.slug}/privacy`} className="project-cta-secondary">
                Privacy Policy
              </Link>
            )}
            {hasSupport && (
              <Link to={`/projects/${project.slug}/support`} className="project-cta-secondary">
                Support
              </Link>
            )}
          </div>
        </div>

        {/* Tech Stack */}
        <section className="project-section">
          <h2>Technology Stack</h2>
          <div className="project-tech-tags">
            {project.techStack.map((tech, index) => (
              <span key={index} className="project-tech-tag">{tech}</span>
            ))}
          </div>
        </section>

        {/* Features */}
        <section className="project-section">
          <h2>Features</h2>
          <div className="project-features-grid">
            {project.features.map((feature, index) => (
              <div key={index} className="project-feature-card">
                <h3>{feature.title}</h3>
                <ul>
                  {feature.items.map((item, i) => (
                    <li key={i}>{item}</li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </section>

        {/* Use Cases */}
        <section className="project-section">
          <h2>Use Cases</h2>
          <ul className="project-use-cases">
            {project.useCases.map((useCase, index) => (
              <li key={index}>{useCase}</li>
            ))}
          </ul>
        </section>

        {/* Installation */}
        {project.installations.length > 0 && (
          <section className="project-section">
            <h2>Getting Started</h2>
            {project.installations.map((install, index) => (
              <div key={index} className="project-install-block">
                {install.title && <h3>{install.title}</h3>}
                {install.description && (
                  <p>{renderTextWithLinks(install.description)}</p>
                )}
                {install.command && (
                  <pre className="project-code-block"><code>{install.command}</code></pre>
                )}
                {install.codeBlock && (
                  <pre className="project-code-block"><code>{install.codeBlock}</code></pre>
                )}
                {install.note && <p className="project-install-note">{install.note}</p>}
              </div>
            ))}
          </section>
        )}

        {/* Links */}
        <section className="project-section">
          <h2>Links</h2>
          <div className="project-links-grid">
            {project.links.map((link, index) => (
              <a
                key={index}
                href={link.url}
                className="project-link-card"
                target="_blank"
                rel="noopener noreferrer"
              >
                <span className="project-link-label">{link.label}</span>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M7 7h10v10" />
                  <path d="M7 17 17 7" />
                </svg>
              </a>
            ))}
          </div>
        </section>

        {/* Footer */}
        <div className="project-page-footer">
          <p>&copy; {new Date().getFullYear()} {project.name} by Apres AI LLC. All rights reserved.</p>
          <p className="project-footer-links">
            <Link to="/projects">All Projects</Link>
            {hasPrivacy && (
              <>
                {' · '}
                <Link to={`/projects/${project.slug}/privacy`}>Privacy</Link>
              </>
            )}
            {hasSupport && (
              <>
                {' · '}
                <Link to={`/projects/${project.slug}/support`}>Support</Link>
              </>
            )}
            {' · '}
            <Link to="/">Apres AI</Link>
          </p>
        </div>
      </div>
    </div>
  );
};

export default ProjectLandingTemplate;
